import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import WhatsAppButton from "@/components/WhatsAppButton";
import TrackingScripts from "@/components/TrackingScripts";
import { InquiryFormProvider } from "@/components/InquiryPopup";

const HomePage = lazy(() => import("@/pages/HomePage"));
const TripsPage = lazy(() => import("@/pages/TripsPage"));
const TripDetailPage = lazy(() => import("@/pages/TripDetailPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const DestinationPage = lazy(() => import("@/pages/DestinationPage"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const HoneymoonPage = lazy(() => import("@/pages/HoneymoonPage"));
const CorporateTripsPage = lazy(() => import("@/pages/CorporateTripsPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const ManageTrips = lazy(() => import("@/pages/admin/ManageTrips"));
const ManageCustomTrips = lazy(() => import("@/pages/admin/ManageCustomTrips"));
const TripForm = lazy(() => import("@/pages/admin/TripForm"));
const ManageBlog = lazy(() => import("@/pages/admin/ManageBlog"));
const BlogForm = lazy(() => import("@/pages/admin/BlogForm"));
const ManageBanners = lazy(() => import("@/pages/admin/ManageBanners"));
const SiteSettings = lazy(() => import("@/pages/admin/SiteSettings"));
const ManageTestimonials = lazy(() => import("@/pages/admin/ManageTestimonials"));
const ManageFAQs = lazy(() => import("@/pages/admin/ManageFAQs"));
const ManageMoments = lazy(() => import("@/pages/admin/ManageMoments"));
const ViewInquiries = lazy(() => import("@/pages/admin/ViewInquiries"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-earth-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-brand border-t-transparent rounded-full animate-spin" />
        <p className="font-body text-sm text-earth-700">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <InquiryFormProvider>
        <ScrollToTop />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/trips" element={<TripsPage />} />
            <Route path="/trip/:slug" element={<TripDetailPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/destinations/:slug" element={<DestinationPage />} />
            <Route path="/honeymoon" element={<HoneymoonPage />} />
            <Route path="/honeymoon/:destination" element={<HoneymoonPage />} />
            <Route path="/corporate-trips" element={<CorporateTripsPage />} />
            <Route path="/policy/:slug" element={<PolicyPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="trips" element={<ManageTrips />} />
              <Route path="trips/new" element={<TripForm />} />
              <Route path="trips/edit/:id" element={<TripForm />} />
              <Route path="custom-trips" element={<ManageCustomTrips />} />
              <Route path="blog" element={<ManageBlog />} />
              <Route path="blog/new" element={<BlogForm />} />
              <Route path="blog/edit/:id" element={<BlogForm />} />
              <Route path="testimonials" element={<ManageTestimonials />} />
              <Route path="moments" element={<ManageMoments />} />
              <Route path="banners" element={<ManageBanners />} />
              <Route path="settings" element={<SiteSettings />} />
              <Route path="faqs" element={<ManageFAQs />} />
              <Route path="inquiries" element={<ViewInquiries />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster richColors position="top-right" />
        <WhatsAppButton />
        <TrackingScripts />
        </InquiryFormProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
